import { useState, type ReactElement } from "react";
import { Menu } from "@base-ui/react/menu";
import { FiCheck, FiCopy, FiLink, FiXCircle } from "react-icons/fi";
import { supabase } from "@baseline/web/supabase";
import { insertAuditLog } from "@baseline/web/auditLog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@baseline/web/auth";

interface ShareButtonProps {
	libraryItemId: string;
	shareToken: string | null;
	contentId?: string;
	documentId?: string;
}

export function ShareButton({
	libraryItemId,
	shareToken,
	contentId,
	documentId,
}: ShareButtonProps): ReactElement {
	const [copied, setCopied] = useState(false);
	const queryClient = useQueryClient();
	const { data: session } = useSession();

	const filterValue = contentId ?? documentId;
	const queryKeyPrefix = contentId ? "library-item" : "library-item-doc";

	const enableSharing = useMutation({
		mutationFn: async () => {
			const token = crypto.randomUUID();
			const { error } = await supabase
				.from("library_items")
				.update({ share_token: token })
				.eq("id", libraryItemId);
			if (error) throw error;
			if (session?.user?.id) {
				await insertAuditLog({
					user_id: session.user.id,
					action: "share",
					entity_type: "library_item",
					entity_id: libraryItemId,
					source: "web",
					changes: { share_token: { old: null, new: token } },
				});
			}
			return token;
		},
		onSuccess: (token) => {
			queryClient.invalidateQueries({
				queryKey: [queryKeyPrefix, filterValue, session?.user?.id],
			});
			const url = `${window.location.origin}/s/${token}`;
			navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		},
	});

	const disableSharing = useMutation({
		mutationFn: async () => {
			const { error } = await supabase
				.from("library_items")
				.update({ share_token: null })
				.eq("id", libraryItemId);
			if (error) throw error;
			if (session?.user?.id) {
				await insertAuditLog({
					user_id: session.user.id,
					action: "unshare",
					entity_type: "library_item",
					entity_id: libraryItemId,
					source: "web",
					changes: { share_token: { old: shareToken, new: null } },
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeyPrefix, filterValue, session?.user?.id],
			});
		},
	});

	function handleCopyLink() {
		if (!shareToken) return;
		const url = `${window.location.origin}/s/${shareToken}`;
		navigator.clipboard.writeText(url);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}

	const menuItemClasses =
		"text-fg-tertiary hover:bg-skeleton hover:text-fg-strong data-[highlighted]:bg-skeleton data-[highlighted]:text-fg-strong flex w-full cursor-default items-center gap-2.5 px-3 py-2 text-sm transition-colors outline-none";

	if (!shareToken) {
		return (
			<button
				type="button"
				onClick={() => enableSharing.mutate()}
				disabled={enableSharing.isPending}
				className="border-line text-fg-faint hover:border-accent-muted hover:text-accent flex h-8 w-8 items-center justify-center rounded-full border transition-colors disabled:opacity-50"
				title="Share link"
			>
				{copied ? (
					<FiCheck className="text-sm text-green-400" />
				) : (
					<FiLink className="text-sm" />
				)}
			</button>
		);
	}

	return (
		<Menu.Root>
			<Menu.Trigger
				className="border-accent-muted text-accent hover:border-accent flex h-8 w-8 items-center justify-center rounded-full border transition-colors"
				title="Sharing options"
			>
				<FiLink className="text-sm" />
			</Menu.Trigger>
			<Menu.Portal>
				<Menu.Positioner side="bottom" align="end" sideOffset={4}>
					<Menu.Popup className="bg-surface-raised border-line min-w-[160px] rounded-lg border py-1 shadow-lg">
						<Menu.Item
							onClick={handleCopyLink}
							className={menuItemClasses}
						>
							{copied ? (
								<FiCheck className="text-xs text-green-400" />
							) : (
								<FiCopy className="text-xs" />
							)}
							Copy share link
						</Menu.Item>
						<Menu.Separator className="border-line my-1 border-t" />
						<Menu.Item
							onClick={() => disableSharing.mutate()}
							className="flex w-full cursor-default items-center gap-2.5 px-3 py-2 text-sm text-red-500 transition-colors outline-none hover:bg-red-50 hover:text-red-600 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-600"
						>
							<FiXCircle className="text-xs" />
							Stop sharing
						</Menu.Item>
					</Menu.Popup>
				</Menu.Positioner>
			</Menu.Portal>
		</Menu.Root>
	);
}
